import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import TemplateDetail from './TemplateDetail';
import { TemplateSummary } from './types';

/** Campaign name is the product — one testing campaign per product. */
function groupOf(t: TemplateSummary): string {
  return t.campaignName || t.campaign?.name || '(no campaign)';
}

const AD_TYPE_ORDER = ['Static', 'Video', 'Short Video'];

export default function TemplateTable({
  templates,
  accountId,
  minSiteLinks,
  busyKey,
  onPatch,
  onRecapture,
  onResync,
  onDelete,
}: {
  templates: TemplateSummary[];
  accountId: string;
  minSiteLinks: number;
  busyKey: string;
  onPatch: (key: string, patch: { name?: string; siteLinksEnabled?: boolean; siteLinksSelected?: string[] }) => void;
  onRecapture: (key: string) => void;
  onResync: (key: string) => void;
  onDelete: (key: string, name: string) => void;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (key: string) =>
    setOpen(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const groups = new Map<string, TemplateSummary[]>();
  for (const t of templates) {
    const g = groupOf(t);
    if (!groups.has(g)) groups.set(g, []);
    groups.get(g)!.push(t);
  }
  for (const list of groups.values()) {
    list.sort((a, b) => AD_TYPE_ORDER.indexOf(a.adType) - AD_TYPE_ORDER.indexOf(b.adType));
  }

  return (
    <Table size="small" sx={{ '& td, & th': { borderBottom: '1px solid', borderColor: 'divider' } }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: 40 }} />
          <TableCell>Template</TableCell>
          <TableCell>Ad type</TableCell>
          <TableCell>Optimising for</TableCell>
          <TableCell>Destination</TableCell>
          <TableCell>Site links</TableCell>
          <TableCell sx={{ width: 40 }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {[...groups.entries()].map(([group, list]) => (
          <React.Fragment key={group}>
            <TableRow>
              <TableCell colSpan={7} sx={{ bgcolor: 'action.hover', py: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700 }}>{group}</Typography>
                {list[0]?.launchable === false && (
                  <Tooltip title="Not a CBO - TESTING campaign. These templates can be kept and inspected, but a launch using one is refused.">
                    <Chip size="small" color="warning" label="cannot launch" sx={{ ml: 1 }} />
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>

            {list.map(t => {
              const expanded = open.has(t.key);
              const busy = busyKey === t.key;
              const invalid = t.siteLinksEnabled && t.selectedSiteLinkCount > 0 && t.selectedSiteLinkCount < minSiteLinks;
              return (
                <React.Fragment key={t.key}>
                  <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggle(t.key)}>
                    <TableCell>
                      <IconButton size="small">
                        {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell><Typography variant="body2">{t.name}</Typography></TableCell>
                    <TableCell><Chip size="small" variant="outlined" label={t.adType} /></TableCell>
                    <TableCell>
                      <Typography variant="caption">{t.adset?.optimization_goal ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 260, display: 'block' }}>
                        {t.creative?.link ? new URL(t.creative.link).pathname : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {t.siteLinksEnabled
                        ? <Chip size="small" color={invalid ? 'error' : 'default'} label={t.selectedSiteLinkCount} />
                        : <Typography variant="caption" color="text.secondary">off</Typography>}
                    </TableCell>
                    <TableCell>{busy && <CircularProgress size={16} />}</TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, borderBottom: expanded ? undefined : 'none' }}>
                      <Collapse in={expanded} unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                          <TemplateDetail
                            template={t}
                            accountId={accountId}
                            minSiteLinks={minSiteLinks}
                            busy={busy}
                            onPatch={patch => onPatch(t.key, patch)}
                            onRecapture={() => onRecapture(t.key)}
                            onResync={() => onResync(t.key)}
                            onDelete={() => onDelete(t.key, t.name)}
                          />
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </React.Fragment>
        ))}
        {templates.length === 0 && (
          <TableRow>
            <TableCell colSpan={7}>
              <Alert severity="info">
                No templates yet. Capture one above — a batch cannot launch without choosing one.
              </Alert>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
}

export { groupOf };
